import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Alert,
  CircularProgress, Stepper, Step, StepLabel, Divider
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import accountService from '../../services/accountService';
import { useAuth } from '../../hooks/useAuth';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after successful closure so the parent can log the user out. */
  onClosureComplete: () => void;
  /** Current account balance in dollars — used in the warning. */
  currentBalance?: number;
}

type Step = 'WARNING' | 'CODE_ENTRY' | 'COMPLETE';

const RESEND_WAIT_SECONDS = 60;

const AccountClosureDialog: React.FC<Props> = ({
  open, onClose, onClosureComplete, currentBalance = 0
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('WARNING');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCountdown, setResendCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Reset state when the dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep('WARNING');
      setMaskedEmail('');
      setCode('');
      setError(null);
      setSending(false);
      setConfirming(false);
      setResendCountdown(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [open]);

  const startCountdown = () => {
    setResendCountdown(RESEND_WAIT_SECONDS);
    timerRef.current = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    setSending(true);
    setError(null);
    try {
      const res = await accountService.requestAccountClosure();
      if (res.success) {
        setMaskedEmail(res.maskedEmail ?? '');
        setStep('CODE_ENTRY');
        startCountdown();
      } else {
        setError(res.message ?? 'Failed to send verification code. Please try again.');
      }
    } catch (e: any) {
      setError(e?.message ?? 'Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0) return;
    setSending(true);
    setError(null);
    setCode('');
    try {
      const res = await accountService.requestAccountClosure();
      if (res.success) {
        setMaskedEmail(res.maskedEmail ?? maskedEmail);
        startCountdown();
      } else {
        setError(res.message ?? 'Failed to resend code.');
      }
    } catch (e: any) {
      setError(e?.message ?? 'Network error.');
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = async () => {
    if (!code.trim()) { setError('Please enter the verification code.'); return; }
    setConfirming(true);
    setError(null);
    try {
      const res = await accountService.confirmAccountClosure(code.trim());
      if (res.success) {
        setStep('COMPLETE');
        // Auto-logout after 4 seconds
        setTimeout(() => { onClosureComplete(); }, 4000);
      } else {
        setError(res.message ?? 'Incorrect or expired code. Please try again.');
      }
    } catch (e: any) {
      setError(e?.message ?? 'Network error. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const stepIndex = step === 'WARNING' ? 0 : step === 'CODE_ENTRY' ? 1 : 2;
  const username = user?.username ?? 'your account';

  return (
    <Dialog
      open={open}
      onClose={step === 'COMPLETE' ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={step === 'COMPLETE'}
    >
      <DialogTitle sx={{ bgcolor: '#b91c1c', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon />
        Close Account Permanently
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stepper activeStep={stepIndex} sx={{ mb: 3 }}>
          <Step><StepLabel>Review Consequences</StepLabel></Step>
          <Step><StepLabel>Verify Identity</StepLabel></Step>
          <Step><StepLabel>Complete</StepLabel></Step>
        </Stepper>

        {/* ── Step 1: Warning ───────────────────────────────────────── */}
        {step === 'WARNING' && (
          <Box>
            <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                This action is permanent and cannot be undone.
              </Typography>
            </Alert>

            <Typography variant="body1" gutterBottom>
              Closing your account for <strong>{username}</strong> will permanently:
            </Typography>

            <Box component="ul" sx={{ pl: 2.5, '& li': { mb: 0.5 } }}>
              <li>
                <Typography variant="body2">
                  Release all your usernames (aliases) — they become available for others to register
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Revoke your access to all messages and account history
                </Typography>
              </li>
              {currentBalance > 0 ? (
                <li>
                  <Typography variant="body2" color="error.main" fontWeight="bold">
                    Forfeit your current balance of ${currentBalance.toFixed(2)} to the Qixit general account
                    — this money cannot be recovered
                  </Typography>
                </li>
              ) : (
                <li>
                  <Typography variant="body2">Your account balance is $0.00</Typography>
                </li>
              )}
              <li>
                <Typography variant="body2">
                  Deactivate all vendor, marketer, and developer credentials
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Deactivate any minor/child accounts linked to your profile
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Update business membership records to show your username as deleted
                  (business aliases you hold in other accounts remain active for those accounts
                  and are unaffected by your GUID)
                </Typography>
              </li>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2">
                Your GUID and all transaction history are <strong>retained for forensic purposes</strong>.
                A new user who later registers your former username will not be associated with
                any of your messages, account history, or business memberships.
              </Typography>
            </Alert>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>
        )}

        {/* ── Step 2: Code Entry ────────────────────────────────────── */}
        {step === 'CODE_ENTRY' && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              A 6-digit verification code has been sent to <strong>{maskedEmail}</strong>.
              It expires in 15 minutes.
            </Alert>

            <Typography variant="body2" gutterBottom>
              Enter the code to confirm permanent account closure:
            </Typography>

            <TextField
              label="Verification Code"
              value={code}
              onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
              fullWidth
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              sx={{ mt: 1, '& input': { fontSize: 28, letterSpacing: 8, textAlign: 'center' } }}
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && code.length === 6) handleConfirm(); }}
            />

            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                disabled={resendCountdown > 0 || sending}
                onClick={handleResend}
              >
                {resendCountdown > 0
                  ? `Resend code (${resendCountdown}s)`
                  : sending ? 'Sending…' : 'Resend code'}
              </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>
        )}

        {/* ── Step 3: Complete ──────────────────────────────────────── */}
        {step === 'COMPLETE' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" gutterBottom color="text.secondary">
              Account Closed
            </Typography>
            <Typography variant="body1" gutterBottom>
              Your account has been permanently closed. A confirmation email has been sent.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You will be logged out in a few seconds…
            </Typography>
            <CircularProgress size={24} sx={{ mt: 2 }} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'WARNING' && (
          <>
            <Button onClick={onClose} disabled={sending}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSendCode}
              disabled={sending}
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {sending ? 'Sending code…' : 'Send Verification Code'}
            </Button>
          </>
        )}
        {step === 'CODE_ENTRY' && (
          <>
            <Button onClick={onClose} disabled={confirming}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirm}
              disabled={confirming || code.length !== 6}
              startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {confirming ? 'Closing Account…' : 'Confirm Account Closure'}
            </Button>
          </>
        )}
        {/* No actions on COMPLETE — auto-logout fires */}
      </DialogActions>
    </Dialog>
  );
};

export default AccountClosureDialog;
